import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Plus, Trash2, Clock } from "lucide-react";
import { useCRMStore } from "../store/crmStore";
import SEO from "./SEO";
import LoadingSpinner from "../../packages/ui/src/components/LoadingSpinner";

export default function AdminCRMNotes() {
  const { id } = useParams<{ id: string }>();
  const {
    selectedContact,
    contactNotes,
    loading,
    error,
    fetchContactDetails,
    addNote,
    deleteNote,
  } = useCRMStore();

  const [newNote, setNewNote] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (id) {
      fetchContactDetails(id);
    }
  }, [id, fetchContactDetails]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !newNote.trim()) return;

    try {
      await addNote(id, newNote.trim());
      setNewNote("");
    } catch (err) {
      console.error("Error adding note:", err);
    }
  };

  const handleDelete = async (noteId: string) => {
    try {
      await deleteNote(noteId);
      setShowDeleteConfirm(null);
    } catch (err) {
      console.error("Error deleting note:", err);
    }
  };

  if (loading) {
    return <LoadingSpinner size="lg" />;
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  if (!selectedContact) {
    return (
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md">
        <p className="text-yellow-700">Contact not found</p>
      </div>
    );
  }

  return (
    <div>
      <SEO
        title="One80Labs - Contact Notes"
        description="Manage notes for contacts in the One80Labs CRM system."
        noindex={true}
      />
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          Notes for {selectedContact.first_name} {selectedContact.last_name}
        </h2>
      </div>

      <div className="bg-white shadow sm:rounded-lg mb-6">
        <div className="px-4 py-5 sm:p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="note"
                className="block text-sm font-medium text-gray-700"
              >
                Add Note
              </label>
              <textarea
                id="note"
                rows={3}
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50"
                placeholder="Enter your note here..."
              />
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={!newNote.trim()}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c] disabled:opacity-50"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Note
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Notes History
          </h3>
          {contactNotes.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No notes yet</p>
          ) : (
            <div className="space-y-4">
              {contactNotes.map((note) => (
                <div
                  key={note.id}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="text-gray-600 whitespace-pre-wrap">
                        {note.note}
                      </p>
                      <div className="mt-2 flex items-center text-sm text-gray-500">
                        <Clock className="h-4 w-4 mr-1" />
                        {new Date(note.created_at).toLocaleString()}
                      </div>
                    </div>
                    <button
                      onClick={() => setShowDeleteConfirm(note.id)}
                      className="ml-4 text-red-400 hover:text-red-500"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Delete Confirmation Modal */}
                  {showDeleteConfirm === note.id && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                      <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">
                          Delete Note
                        </h3>
                        <p className="text-sm text-gray-500 mb-4">
                          Are you sure you want to delete this note? This action
                          cannot be undone.
                        </p>
                        <div className="flex justify-end space-x-3">
                          <button
                            onClick={() => setShowDeleteConfirm(null)}
                            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleDelete(note.id)}
                            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
